/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['modalContent', 'modalOverlay', 'close', 'modalClose'] }] */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ConnectedProps } from 'react-redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import type { AppState } from 'types/app';
import type { EmptyObject } from 'types/utility';
import type { CartItem as CartItemType, CartResponse } from 'types/mafia';
import type { PostPurchaseDataParams } from 'types/stripe';
import { isApplePayAvailable } from 'utils/applePay';
import { cn } from 'helpers/classnames';
import useMartyContext from 'hooks/useMartyContext';
import { PAGE_TYPES } from 'constants/appConstants';
import { pageTypeChange } from 'actions/common';
import { CART_PAGE_MODAL } from 'constants/amethystPageTypes';
import { onCartModalClose, onCartView, onViewCartClick } from 'store/ducks/cart/actions';
import { fetchCartItems, navigateToCheckout, showCartModal } from 'actions/cart';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import JanusPixel from 'components/common/JanusPixel';
import { ensureNoClass, pluralize } from 'helpers/index';
import { toUSD } from 'helpers/NumberFormats';
import { groupCartItemsBySizePrediction } from 'helpers/CartUtils';
import { removeTrailingZeroes } from 'helpers/ProductUtils';
import useEvent from 'hooks/useEvent';
import FocusTrap from 'components/common/FocusTrap';
import { SmallLoader } from 'components/Loader';
import EmptyCart from 'components/cart/EmptyCart';
import CartErrors from 'components/cart/CartErrors';
import RewardsTransparency from 'components/common/RewardsTransparency';
import LoginLink from 'components/common/LoginLink';
import useEffectOnce from 'hooks/useEffectOnce';
import { trackArtificialPageViewModalOpen } from 'helpers/contentSquare/analytics';
import { useKratosCheckoutEnabled } from 'hooks/useKratosCheckoutEnabled';
import CloseIcon from 'components/common/IconsV2/CloseIcon';
import CheckFilledIcon from 'components/common/IconsV2/CheckFilledIcon';
import NewCartItem from 'components/cart/NewCartItem/NewCartItem';
import { ApplePay } from 'components/ApplePay';
import { configureCheckout } from 'apis/checkout';
import { useIsEnrolledInVariant } from 'hooks/unleash/useIsEnrolledInVariant';
import { FEATURE_CART_APPLE_PAY, FEATURE_CHECKOUT_LATENCY_OPTIMIZATION, KILLSWITCH_APPLE_PAY_CART } from 'constants/features';
import { useUnleashKillswitch } from 'hooks/unleash/useUnleashKillswitch';
import { CONFIGURE_CHECKOUT_SUCCESS } from 'store/ducks/checkout/types';
import { selectCheckoutData } from 'selectors/checkout';
import { selectCookies } from 'selectors/cookies';
import { initiateClickToDisplayAction } from 'helpers/clickToDisplay/initiateClickToDisplayAction';
import { trackError } from 'helpers/ErrorUtils';

import modalCSS from 'styles/components/common/modal.scss';
import css from 'styles/components/cart/cartModal.scss';

type PropsFromRedux = ConnectedProps<typeof connector>;

const isNonEmptyCartObject = (response: CartResponse | EmptyObject): response is CartResponse =>
  (response as CartResponse).activeItems !== undefined;

export const CartModal = (props: PropsFromRedux) => {
  const { purchase: purchaseState } = useSelector(selectCheckoutData);
  const dispatch = useDispatch();
  const { testId, router, marketplace } = useMartyContext();
  const isZapposMarketplace = marketplace.theme === 'zappos';
  const {
    api: { mafia },
    cart: { cartName, showFitRecommendations },
    checkout: { checkoutUrl, stripeApiKey }
  } = marketplace;
  const [sourcePageType, setSourcePageType] = useState();
  const [isClosing, setIsClosing] = useState(false);
  const isKratosCheckoutEnabled = useKratosCheckoutEnabled();
  const {
    cart: { cartObj: cart, cartCount, loading, isLoaded, isModalShowing, addedStockId },
    fetchCartItems,
    showCartModal,
    onCartView,
    onCartModalClose,
    onViewCartClick,
    pageTypeChange,
    isRecognizedCustomer,
    navigateToCheckout,
    sourcePageType: sourcePageTypeProp
  } = props;
  const cookies = useSelector(selectCookies);
  const { isEnrolled: isApplePayEnrolled } = useIsEnrolledInVariant(FEATURE_CART_APPLE_PAY);
  const { isEnrolled: isLatencyOptimizationEnabled } = useIsEnrolledInVariant(FEATURE_CHECKOUT_LATENCY_OPTIMIZATION);
  const { isKillswitchOn: isApplePayDisabled } = useUnleashKillswitch(KILLSWITCH_APPLE_PAY_CART);

  const { activeItems = [], savedItems = [], subtotal } = cart;
  const isApplePayEnabled = isApplePayAvailable() && !isApplePayDisabled && isRecognizedCustomer && isApplePayEnrolled;
  const isEarlyPurchaseCreateEnabled = isLatencyOptimizationEnabled && isRecognizedCustomer && isKratosCheckoutEnabled;
  const hasPurchase = !!Object.keys(purchaseState).length;
  const configureCheckoutInFlight = useRef(false);
  const configureCheckoutPromise = useRef<Promise<string | undefined> | null>(null);
  const [isAwaitingPurchase, setIsAwaitingPurchase] = useState(false);

  // fetch cart
  useEffect(() => {
    if (!isLoaded) {
      fetchCartItems().then(response => {
        onCartView({ ...response, isCartModal: true });
      });
    } else if (isNonEmptyCartObject(cart)) {
      onCartView({ ...cart, isCartModal: true });
    }
  }, [cart, fetchCartItems, isLoaded, onCartView]);

  // scrolling to top + scroll lock
  useEffect(() => {
    toggleDesktopModal();
    // on unmount remove the scrollLock class to allow scrolling again
    return () => {
      ensureNoClass(document.documentElement, 'scrollLock');
    };
  }, []);

  const loadPurchase = useCallback(async () => {
    const { payload } = await configureCheckout({ url: mafia.url }, { reqData: { useDiscount: true, useGCBalance: true } }, cookies);
    const { purchaseStatus } = payload;

    dispatch({ type: CONFIGURE_CHECKOUT_SUCCESS, payload: { purchaseStatus } });
    return purchaseStatus?.purchaseId;
  }, []);

  const updatePurchase = useCallback(async (reqData: PostPurchaseDataParams) => {
    const response = await configureCheckout({ url: mafia.url }, { reqData }, cookies);
    const { purchaseStatus } = response.payload;

    dispatch({ type: CONFIGURE_CHECKOUT_SUCCESS, payload: { purchaseStatus } });

    return purchaseStatus;
  }, []);

  // Guarded purchase init: skips if a call is already in-flight, resets guard on completion.
  const doInitPurchase = useCallback(
    (path: string) => {
      if (configureCheckoutInFlight.current) return;
      configureCheckoutInFlight.current = true;
      const promise = loadPurchase()
        .catch(e => {
          trackError('NON-FATAL', `Eager purchase creation failed (${path})`, e);
          return undefined;
        })
        .finally(() => {
          configureCheckoutInFlight.current = false;
          configureCheckoutPromise.current = null;
        });
      configureCheckoutPromise.current = promise;
    },
    [loadPurchase]
  );

  // set original page type so we can restore it when the modal closes
  useEffectOnce(() => {
    if (isApplePayEnabled && !hasPurchase) {
      doInitPurchase('apple-pay');
    }
    trackArtificialPageViewModalOpen({ query: 'cart-modal', pageType: 'cartModal' });
    setSourcePageType(sourcePageTypeProp);
    pageTypeChange(PAGE_TYPES.CART_MODAL);
  });

  // Eagerly create purchase doc for authenticated users to skip initPurchase() on kratos checkout entry
  useEffect(() => {
    if (isEarlyPurchaseCreateEnabled && !hasPurchase) {
      doInitPurchase('kratos-checkout-latency-optimization');
    }
  }, [isEarlyPurchaseCreateEnabled, hasPurchase, doInitPurchase]);

  useEvent(document.body, 'keydown', ((e: KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      onHideModal();
    }
  }) as EventListener);

  const toggleDesktopModal = () => {
    document.documentElement.classList.toggle('scrollLock');
    const root: HTMLElement | null = document.getElementById('root');

    if (root) {
      if (root.hasAttribute('aria-hidden')) {
        root.removeAttribute('aria-hidden');
      } else {
        root.setAttribute('aria-hidden', 'true');
      }
    }
  };

  const onHideModal = () => {
    pageTypeChange(sourcePageType);
    setIsClosing(true);
    toggleDesktopModal();
    setTimeout(() => {
      showCartModal(false);
    }, 500); // give CSS a chance to fade before removing from DOM

    onCartModalClose();
  };

  const onClickedViewCart = () => {
    onViewCartClick();
    onHideModal();
  };

  const onCheckingOut = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Track all possible checkout destinations since we don't know where user will land
    initiateClickToDisplayAction('cart_modal:login');
    initiateClickToDisplayAction('cart_modal:shipping');
    initiateClickToDisplayAction('cart_modal:payment');
    initiateClickToDisplayAction('cart_modal:review');

    // If configureCheckout is still in flight, wait for it so we can navigate with the purchaseId
    if (isEarlyPurchaseCreateEnabled && configureCheckoutInFlight.current && configureCheckoutPromise.current) {
      setIsAwaitingPurchase(true);
      const resolvedPurchaseId = await configureCheckoutPromise.current;
      setIsAwaitingPurchase(false);
      onHideModal();
      navigateToCheckout(router, CART_PAGE_MODAL, isKratosCheckoutEnabled, resolvedPurchaseId ?? purchaseState?.purchaseId);
      return;
    }

    onHideModal();
    navigateToCheckout(router, CART_PAGE_MODAL, isKratosCheckoutEnabled, purchaseState?.purchaseId);
  };

  const makeEmptyCart = () => (
    <EmptyCart onLinkClick={onHideModal} className={css.empty} isRecognizedCustomer={isRecognizedCustomer}>
      {!isRecognizedCustomer && (
        <div className={css.signIn}>
          <LoginLink onClick={() => showCartModal(false)} data-test-id={testId('signInButton')}>
            Sign In
          </LoginLink>
        </div>
      )}
    </EmptyCart>
  );

  const makeCartItems = () => {
    // Create three sets of cartItems, those with both correct and incorrect size predictions, and those without
    const { itemsWithFitRecommended, itemsWithFitNotRecommended, itemsWithNoRecommendationAvailable } = groupCartItemsBySizePrediction(activeItems);
    const numberOfNotRecommended = itemsWithFitNotRecommended.length;

    return (
      <>
        <div className={cn(css.itemContainer, 'flex flex-col gap-y-6 px-4 pt-4')}>
          <CartErrors className='"w-full p-2"' />
          {showFitRecommendations ? (
            <>
              {Boolean(numberOfNotRecommended) && (
                <div className={css.fitNotRecommended}>
                  <p className={css.fitCallout}>
                    {numberOfNotRecommended === 1 ? 'This' : 'These'} {pluralize('size', numberOfNotRecommended)} might not fit you!
                  </p>
                  <p>
                    Our handy-dandy sizing feature suggests that {pluralize('this', numberOfNotRecommended)}{' '}
                    {pluralize('size', numberOfNotRecommended)} might not be the best fit for you.
                  </p>
                  {itemsWithFitNotRecommended.map((item: CartItemType) => (
                    <NewCartItem item={item} key={item.cartItemId} />
                  ))}
                </div>
              )}
              {itemsWithFitRecommended.map((item: CartItemType) => (
                <NewCartItem item={item} key={item.cartItemId} isRecommendedFit />
              ))}

              {itemsWithNoRecommendationAvailable.map((item: CartItemType) => (
                <NewCartItem item={item} key={item.cartItemId} />
              ))}
            </>
          ) : (
            <>
              {activeItems.map(item => (
                <NewCartItem item={item} key={item.cartItemId} />
              ))}
            </>
          )}
          {savedItems?.map(item => <NewCartItem item={item} key={item.cartItemId} isUnavailable />)}
        </div>
        {makeCartFooter()}
      </>
    );
  };

  const makeCartFooter = () =>
    isZapposMarketplace ? (
      <div data-test-id={testId('cartModalFooter')} className={css.footer}>
        <p className={css.subtotal}>
          <Link to="/cart" onClick={onClickedViewCart} className={cn(css.viewBagLink, 'max-md:hidden')} data-test-id={testId('viewCart')}>
            <span className="flex items-center gap-x-2 border-b py-px pb-[1px] hover:border-b-2 hover:pb-0">View {cartName}</span>
          </Link>
          <div className="max-md:flex max-md:w-full max-md:justify-between">
            <span data-test-id={testId('cartQuantity')}>
              {cartName} Subtotal ({cartCount} {pluralize('item', cartCount)})
            </span>
            {subtotal && <span data-test-id={testId('subtotal')}>{removeTrailingZeroes(toUSD(subtotal.amount))}</span>}
          </div>
        </p>
        <form
          className={cn({ '!pt-2': !isApplePayEnabled || !hasPurchase })}
          onSubmit={onCheckingOut}
          action={`${checkoutUrl}/initiate`}
          method="post"
        >
          <button
            type="submit"
            disabled={loading || isAwaitingPurchase}
            className={cn(css.primaryCTA, 'w-full leading-none')}
            data-test-id={testId('proceedToCheckout')}
          >
            {loading ? 'Updating...' : 'Checkout'}
          </button>
        </form>
        {isApplePayEnabled && hasPurchase && !loading && (
          <div className="mt-3 h-10">
            <ApplePay
              apiKey={stripeApiKey}
              isGuest={false}
              purchaseState={purchaseState}
              theme="cart"
              updatePurchase={updatePurchase}
              onClose={onHideModal}
            />
          </div>
        )}
      </div>
    ) : (
      <div data-test-id={testId('cartModalFooter')} className={css.footer}>
        <p className={css.subtotal}>
          <span data-test-id={testId('cartQuantity')}>
            {cartName} Subtotal ({cartCount} {pluralize('item', cartCount)})
          </span>
          {subtotal && <span data-test-id={testId('subtotal')}>{removeTrailingZeroes(toUSD(subtotal.amount))}</span>}
        </p>

        <form onSubmit={onCheckingOut} action={`${checkoutUrl}/initiate`} method="post">
          <Link to="/cart" onClick={onClickedViewCart} className={cn(css.secondaryCTA, 'zappos:leading-none')} data-test-id={testId('viewCart')}>
            View {cartName}
          </Link>

          <button
            type="submit"
            disabled={loading || isAwaitingPurchase}
            className={cn(css.primaryCTA, 'zappos:leading-none')}
            data-test-id={testId('proceedToCheckout')}
          >
            {loading ? 'Updating...' : 'Proceed to Checkout'}
          </button>
        </form>
      </div>
    );

  const makeHeader = () => (
    <p className={cn(css.header, 'zappos:heading-m')} id="modalDescription" data-test-id={testId('cartModalHeader')}>
      {addedStockId ? (
        <>
          <span className={css.check} />
          <CheckFilledIcon className="mr-1" size={24} />
          Added to {cartName}
        </>
      ) : (
        `My ${cartName}`
      )}
    </p>
  );

  const makeJanusPixel = () => {
    const addedProduct = activeItems?.find(item => item.stockId === addedStockId);

    if (!addedProduct) {
      return null;
    }

    const { stockId, styleId } = addedProduct;

    const queryParams = {
      teen: styleId,
      child: stockId,
      widget: 'RecordCart'
    };

    return <JanusPixel queryParams={queryParams} />;
  };

  if (!isModalShowing) {
    return null;
  }

  return createPortal(
    <FocusTrap active={isModalShowing} shouldFocusFirstElement>
      {focusRef => (
        <>
          <div
            className={css.container}
            role="dialog"
            aria-modal="true"
            aria-describedby="modalDescription"
            ref={focusRef as React.MutableRefObject<HTMLDivElement>}
          >
            {addedStockId && makeJanusPixel()}
            <div className={cn(css.modal, { [css.loading]: !isLoaded, [css.isClosing]: isClosing })} data-test-id={testId('cartModal')}>
              <div className={cn(modalCSS.header, css.modalHeader)}>
                {makeHeader()}
                <button type="button" onClick={onHideModal} data-test-id={testId('closeModal')} aria-label="Close">
                  <CloseIcon size={32} />
                </button>
              </div>
              {isLoaded && cart ? (
                <>
                  <RewardsTransparency isForCartModal={true} />
                  {activeItems.length ? makeCartItems() : makeEmptyCart()}
                </>
              ) : (
                <SmallLoader />
              )}
            </div>
          </div>
          {/* eslint-disable jsx-a11y/no-static-element-interactions */}
          {/*
            react-modal does onclick on divs
            https://github.com/reactjs/react-modal/blob/master/src/components/ModalPortal.js
          */}
          <div onClick={onHideModal} onKeyPress={onHideModal} className={cn(css.wrapper, { [css.isClosing]: isClosing })} />
        </>
      )}
    </FocusTrap>,
    document.body
  );
};

export function mapStateToProps(state: AppState) {
  const {
    cart,
    customer: isRecognizedCustomer,
    pageView: { pageType: sourcePageType }
  } = state;

  return {
    cart,
    isRecognizedCustomer,
    sourcePageType
  };
}

const mapDispatchToProps = {
  fetchCartItems,
  showCartModal,
  navigateToCheckout,
  onCartView,
  onCartModalClose,
  onViewCartClick,
  pageTypeChange
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const ConnectedCartModal = connector(CartModal);
export default withErrorBoundary('CartModal', ConnectedCartModal);
